import React from "react";
import PropTypes from "prop-types";
import { useSelector } from "react-redux";
import { Link } from "react-router-dom";
import dayjs from "dayjs";
import "dayjs/locale/ar";
import { FileText } from "lucide-react";

import messages from "../../assets/locale/messages";
import { ROUTE_PATHS } from "../../utils/RoutesPaths";
import TruncatedWithTooltip from "../TruncatedWithTooltip";
import {
  UIAvatar,
  UIChip,
  UIRowActionsMenu,
  UIStatusTag,
  UITable,
} from "../ui";
import {
  REQUEST_STATUSES,
  REQUEST_TABS,
  REQUEST_TYPES,
  fillTemplate,
  formatAmount,
  getCompanyName,
} from "./publishingRequestsUtils";

import "./PublishingRequests.scss";

const EMPTY_VALUE = "-";

const TYPE_CHIP_COLORS = {
  [REQUEST_TYPES.publish]: "secondary",
  [REQUEST_TYPES.crewSizeChange]: "warning",
};

/** Status keys the shared status tag has styles for. */
const STATUS_TAG_KEYS = {
  [REQUEST_STATUSES.accepted]: "approved",
  [REQUEST_STATUSES.rejected]: "canceled",
  [REQUEST_STATUSES.cancelled]: "canceled",
};

/** Company and event names get twice the share of the other, equally sized, columns. */
const WIDE_COLUMN = { flex: 2, minWidth: 260 };

const TextCell = ({ value }) => (
  <TruncatedWithTooltip title={value}>
    <span className="MuiDataGrid-cellContent">{value}</span>
  </TruncatedWithTooltip>
);

TextCell.propTypes = {
  value: PropTypes.string.isRequired,
};

/** The name opens its page; plain text when the id is missing. */
const LinkCell = ({ value, to }) =>
  to ? (
    <TruncatedWithTooltip title={value}>
      <Link to={to} className="MuiDataGrid-cellContent publishing-requests-table__link">
        {value}
      </Link>
    </TruncatedWithTooltip>
  ) : (
    <TextCell value={value} />
  );

LinkCell.propTypes = {
  value: PropTypes.string.isRequired,
  to: PropTypes.string,
};

LinkCell.defaultProps = {
  to: "",
};

const PublishingRequestsTable = ({
  highlightedId,
  rows,
  tab,
  onAccept,
  onReject,
  onPreviewProof,
}) => {
  const lang = useSelector((state) => state.locale.lang);
  const { publishingRequests: t, shared } = messages[lang];
  const isPendingTab = tab === REQUEST_TABS.pending;

  const column = (field, headerName, renderCell, options = {}) => ({
    field,
    headerName,
    flex: 1,
    minWidth: 150,
    sortable: false,
    renderCell: ({ row }) => renderCell(row),
    ...options,
  });

  const columns = [
    column(
      "company",
      t.companyName,
      (row) => {
        const name = getCompanyName(row.company, lang) || EMPTY_VALUE;
        return (
          <div className="publishing-requests-table__company">
            <UIAvatar
              src={row.company?.image_url}
              name={name}
              size="sm"
              className="flex-shrink-0"
            />
            <LinkCell
              value={name}
              to={
                row.company?.id != null
                  ? ROUTE_PATHS.companiesListCompanyDetails.replace(":id", row.company.id)
                  : ""
              }
            />
          </div>
        );
      },
      WIDE_COLUMN
    ),
    column(
      "event",
      t.eventName,
      (row) => (
        <LinkCell
          value={row.event?.name || EMPTY_VALUE}
          to={
            row.event?.id != null
              ? ROUTE_PATHS.viewEvent.replace(":id", row.event.id)
              : ""
          }
        />
      ),
      WIDE_COLUMN
    ),
    column(
      "type",
      t.requestType,
      (row) => (
        <UIChip
          size="small"
          appearance="light"
          color={TYPE_CHIP_COLORS[row.type]}
          label={t.types[row.type]}
        />
      ),
      // Wide enough for the longest chip, "Crew Size Change", in both languages (PROLINE-4184).
      { minWidth: 190 }
    ),
    column("amount", t.feesAmount, (row) => (
      <TextCell
        value={fillTemplate(t.amountValue, {
          amount: formatAmount(row.amount),
        })}
      />
    )),
    column("requested_at", t.requestDate, (row) => (
      <TextCell
        value={
          row.requested_at
            ? dayjs(row.requested_at)
                .locale(lang === "ar" ? "ar" : "en")
                .format("DD MMM YYYY")
            : EMPTY_VALUE
        }
      />
    )),
    ...(isPendingTab
      ? [
          column(
            "payment_proof",
            t.paymentProof,
            (row) =>
              row.payment_proof ? (
                <button
                  type="button"
                  className="publishing-requests-table__proof"
                  onClick={() => onPreviewProof(row.payment_proof)}
                >
                  <span className="publishing-requests-table__proof-icon">
                    <FileText size={16} aria-hidden />
                  </span>
                  {/* The full name shows on hover once it is cut off (PROLINE-4186). */}
                  <TruncatedWithTooltip title={row.payment_proof.name}>
                    <span
                      className="text-label-sm publishing-requests-table__proof-name"
                      // A Latin file name stays left-to-right in Arabic, so it is cut at its end.
                      dir="auto"
                    >
                      {row.payment_proof.name}
                    </span>
                  </TruncatedWithTooltip>
                </button>
              ) : (
                <TextCell value={EMPTY_VALUE} />
              ),
            WIDE_COLUMN
          ),
          column(
            "actions",
            shared.actions,
            (row) => (
              <UIRowActionsMenu
                ariaLabel={shared.actions}
                items={[
                  {
                    key: "accept",
                    label: t.accept,
                    success: true,
                    onClick: () => onAccept(row),
                  },
                  {
                    key: "reject",
                    label: t.reject,
                    danger: true,
                    onClick: () => onReject(row),
                  },
                ]}
              />
            ),
            { align: "center", headerAlign: "center" }
          ),
        ]
      : [
          column("status", t.requestStatus, (row) => (
            <UIStatusTag
              preset="table"
              status={STATUS_TAG_KEYS[row.status] ?? row.status}
              label={t.statuses[row.status]}
            />
          )),
        ]),
  ];

  return (
    <UITable
      rows={rows}
      columns={columns}
      contentSized
      hideFooter
      className="publishing-requests-table"
      getRowClassName={({ id }) =>
        String(id) === String(highlightedId)
          ? "publishing-requests-table__row--highlighted"
          : ""
      }
    />
  );
};

PublishingRequestsTable.propTypes = {
  /** The request a notification points at: its row is highlighted. */
  highlightedId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  rows: PropTypes.arrayOf(PropTypes.object).isRequired,
  tab: PropTypes.oneOf(Object.values(REQUEST_TABS)).isRequired,
  onAccept: PropTypes.func.isRequired,
  onReject: PropTypes.func.isRequired,
  onPreviewProof: PropTypes.func.isRequired,
};

PublishingRequestsTable.defaultProps = {
  highlightedId: null,
};

export default PublishingRequestsTable;
